/**
 * ExpensesTab — Finance sub-tab for team expenses (2026-07-11, batch-B G1
 * / G6). Visible ALWAYS for coaches (no settings gate) per the task spec —
 * expenses are core to "Finance" regardless of whether spare/season-fee
 * COLLECTION is turned on.
 *
 * Reuses the mig-099 budget ledger (budget_transactions) rather than
 * building a parallel expense table, but talks to a NEW small endpoint
 * set — GET/POST/PATCH/DELETE /teams/:teamId/budget/finance-expenses
 * (backend/src/routes/budget.js) — instead of the module's normal
 * /transactions endpoints. Why: every route in budget.js sits behind
 * requireBudgetEnabled (settings.budget_enabled, a super-admin toggle,
 * OFF by default). The 2026-07-10 schedule-import "confirm" flow ALREADY
 * writes budget_transactions expense rows unconditionally (see
 * schedule-import.js) regardless of that flag — so a team that never
 * flipped budget_enabled could have real expense rows sitting in the
 * table with no way to READ them back through the normal budget API.
 * The finance-expenses routes are scoped to kind='expense' only (never
 * touch income rows) and gate on coach/manager role instead of
 * budget_enabled, so this tab works for every coach team, matching
 * Gilles's "expenses live at home in Finance" request. Full
 * categorization / receipts / wallet stay behind the real Budget module
 * (Team Settings-enabled) at /team/:teamId/budget.
 */
import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import api from '../../services/api.js';
import toast from 'react-hot-toast';

const fmtMoney = (cents, currency = 'cad') => {
  const amount = (cents || 0) / 100;
  const ccy = (currency || 'cad').toUpperCase();
  return `${ccy} ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const centsFromInput = (v) => {
  const n = parseFloat(String(v).replace(/[^0-9.\-]/g, ''));
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : null;
};
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function ExpensesTab({ teamId }) {
  const [loading, setLoading] = useState(true);
  const [txns, setTxns] = useState([]);
  const [ftCount, setFtCount] = useState(0);
  const [error, setError] = useState(false);

  // Add-expense free-form fields
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);

  // Inline edit state for a single row being confirmed/edited: { id, amount }
  const [editRow, setEditRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await api.get(`/teams/${teamId}/budget/finance-expenses`);
      setTxns(data.transactions || []);
      setFtCount(data.fullTimePlayerCount || 0);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => { load(); }, [load]);

  const addExpense = async () => {
    const trimmedName = name.trim();
    const cents = centsFromInput(amount);
    if (!trimmedName) { toast.error('Enter an expense name'); return; }
    if (cents == null) { toast.error('Enter a valid amount'); return; }
    setSaving(true);
    try {
      await api.post(`/teams/${teamId}/budget/finance-expenses`, {
        name: trimmedName, amount_cents: cents, currency: 'cad', txn_date: todayISO(),
      });
      setName('');
      setAmount('');
      toast.success('Expense added');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to add expense');
    } finally {
      setSaving(false);
    }
  };

  const confirmRow = async (id, newAmountCents) => {
    try {
      await api.patch(`/teams/${teamId}/budget/finance-expenses/${id}`, { amount_cents: newAmountCents, confirm: true });
      toast.success('Confirmed');
      setEditRow(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to confirm');
    }
  };

  const deleteRow = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await api.delete(`/teams/${teamId}/budget/finance-expenses/${id}`);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to delete');
    }
  };

  const byCcy = {};
  txns.forEach(t => { const c = (t.currency || 'cad').toLowerCase(); byCcy[c] = (byCcy[c] || 0) + (t.amount_cents || 0); });
  const primaryCcy = Object.keys(byCcy)[0] || 'cad';
  const total = byCcy[primaryCcy] || 0;

  if (loading) return <p className="text-secondary text-sm py-6 text-center">Loading expenses…</p>;
  if (error) return <p className="text-secondary text-sm py-6 text-center">Couldn't load expenses.</p>;

  return (
    <div>
      {/* Totals */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
        <div className="card">
          <h3 className="font-semibold text-sm mb-2">Total expenses</h3>
          <div className="font-bold" style={{ fontSize: 22, color: 'var(--penalty)', fontFamily: 'var(--font-display)' }}>
            {fmtMoney(total, primaryCcy)}
          </div>
          {Object.keys(byCcy).length > 1 && (
            <p className="text-xs text-secondary mt-1">
              Other currencies: {Object.entries(byCcy).filter(([c]) => c !== primaryCcy)
                .map(([c, cents]) => fmtMoney(cents, c)).join(', ')}
            </p>
          )}
        </div>
        <div className="card">
          <h3 className="font-semibold text-sm mb-2">Est. cost per full-time player</h3>
          {ftCount > 0 ? (
            <>
              <div className="font-bold" style={{ fontSize: 22, color: 'var(--ice)', fontFamily: 'var(--font-display)' }}>
                {fmtMoney(Math.round(total / ftCount), primaryCcy)}
              </div>
              <p className="text-xs text-secondary mt-1">
                {fmtMoney(total, primaryCcy)} ÷ {ftCount} full-time player{ftCount === 1 ? '' : 's'} (goalies excluded)
              </p>
            </>
          ) : (
            <p className="text-xs text-secondary">No full-time players tracked yet.</p>
          )}
        </div>
      </div>

      {/* Add expense */}
      <div className="card mb-5">
        <h3 className="font-semibold text-sm mb-3">Add expense</h3>
        <div className="flex gap-2 flex-wrap items-end">
          <div style={{ flex: '2 1 200px' }}>
            <label className="label">Name</label>
            <input
              type="text" className="input-field" placeholder="e.g. Ice rental — July"
              value={name} onChange={e => setName(e.target.value)} maxLength={200}
            />
          </div>
          <div style={{ flex: '1 1 120px' }}>
            <label className="label">Amount</label>
            <input
              type="text" inputMode="decimal" className="input-field" placeholder="0.00"
              value={amount} onChange={e => setAmount(e.target.value)}
            />
          </div>
          <button type="button" className="btn-ice" disabled={saving} onClick={addExpense}>
            <Plus size={15} style={{ verticalAlign: -2, marginRight: 4 }} />
            {saving ? 'Adding…' : 'Add expense'}
          </button>
        </div>
      </div>

      {/* List */}
      <div className="card">
        <h3 className="font-semibold text-sm mb-3">Expense log</h3>
        {txns.length === 0 ? (
          <p className="text-secondary text-sm py-4 text-center">No expenses recorded yet.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="w-full text-sm" style={{ minWidth: 560 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th className="text-left py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }}>Date</th>
                  <th className="text-left py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }}>Description</th>
                  <th className="text-right py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }}>Amount</th>
                  <th className="text-right py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }}></th>
                </tr>
              </thead>
              <tbody>
                {txns.map(t => {
                  const editing = editRow?.id === t.id;
                  return (
                    <tr key={t.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td className="py-2 px-2 whitespace-nowrap">{t.txn_date}</td>
                      <td className="py-2 px-2">
                        {t.memo || <span style={{ color: 'var(--text-3)' }}>—</span>}
                        {/* G6 — imported-from-schedule rows are flagged via
                            source='import' (schedule-import.js writes these
                            unconditionally when a permit total is captured).
                            needsConfirm comes straight from the backend. */}
                        {t.needsConfirm && !editing && (
                          <div className="mt-1">
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
                              style={{ background: 'rgba(255,149,0,0.12)', color: 'var(--puck)', border: '1px solid rgba(255,149,0,0.3)' }}>
                              <AlertTriangle size={11} /> imported from schedule — confirm amount
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right" style={{ whiteSpace: 'nowrap' }}>
                        {editing ? (
                          <input
                            type="text" inputMode="decimal" className="input-field py-1"
                            style={{ width: 90, textAlign: 'right', display: 'inline-block' }}
                            value={editRow.amount}
                            onChange={e => setEditRow(r => ({ ...r, amount: e.target.value }))}
                            autoFocus
                          />
                        ) : (
                          <span style={{ color: 'var(--penalty)', fontWeight: 700 }}>
                            {fmtMoney(t.amount_cents, t.currency)}
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right" style={{ whiteSpace: 'nowrap' }}>
                        {editing ? (
                          <>
                            <button
                              type="button" className="btn-ice text-xs px-2 py-1"
                              onClick={() => {
                                const cents = centsFromInput(editRow.amount);
                                if (cents == null) { toast.error('Enter a valid amount'); return; }
                                confirmRow(t.id, cents);
                              }}>
                              <CheckCircle2 size={12} style={{ verticalAlign: -1, marginRight: 3 }} />
                              Confirm
                            </button>
                            <button
                              type="button" className="text-xs px-2 py-1 ml-1"
                              style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', color: 'var(--text-2)' }}
                              onClick={() => setEditRow(null)}>
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button" className="text-xs px-2 py-1"
                              style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', color: 'var(--text-2)' }}
                              onClick={() => setEditRow({ id: t.id, amount: (t.amount_cents / 100).toFixed(2) })}>
                              {t.needsConfirm ? 'Edit / confirm' : 'Edit'}
                            </button>
                            <button
                              type="button" className="text-xs px-2 py-1 ml-1"
                              style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', color: 'var(--penalty)' }}
                              onClick={() => deleteRow(t.id)}>
                              <Trash2 size={12} />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
